import { useParams } from "src/router";
import {
  useDestinationQuery,
  useEventWarehouseDestinationQuery,
} from "src/graphql";
import { getEventDestinationRouteType } from "./event-destination-id";
import { ForwardingDestination, WarehouseDestination } from "./types";
import {
  EventDestinationOutletContext,
  useEventDestinationOutletContextIfAvailable,
} from "./outlet";

export function useEventDestinationRouteContext(): {
  context: EventDestinationOutletContext | undefined;
  isLoading: boolean;
} {
  const outletContext = useEventDestinationOutletContextIfAvailable();
  const { id } = useParams<{ id: string }>();
  const destinationType = getEventDestinationRouteType(id);
  const isWarehouseDestination = destinationType === "warehouse";

  const { data: forwardingDestination, isLoading: forwardingIsLoading } =
    useDestinationQuery(
      { id: String(id) },
      {
        enabled: !outletContext && Boolean(id) && !isWarehouseDestination,
        select: (data) => data.destinations_by_pk,
      },
    );

  const { data: warehouseDestination, isLoading: warehouseIsLoading } =
    useEventWarehouseDestinationQuery(
      { id: String(id) },
      {
        enabled: !outletContext && Boolean(id) && isWarehouseDestination,
        select: (data) => data.event_warehouse_destinations_by_pk,
      },
    );

  if (outletContext) {
    return {
      context: outletContext,
      isLoading: false,
    };
  }

  if (!id) {
    return {
      context: undefined,
      isLoading: false,
    };
  }

  if (isWarehouseDestination) {
    return {
      context: warehouseDestination
        ? {
            type: "warehouse",
            destination: warehouseDestination as WarehouseDestination,
          }
        : undefined,
      isLoading: warehouseIsLoading,
    };
  } else if (destinationType === "forwarding") {
    return {
      context: forwardingDestination
        ? {
            type: "forwarding",
            destination: forwardingDestination as ForwardingDestination,
          }
        : undefined,
      isLoading: forwardingIsLoading,
    };
  } else {
    return {
      context: undefined,
      isLoading: false,
    };
  }
}
